import type { AccountKind, MarshalDeadline, MarshalInstitution, StepKind } from './api/marshalling.ts';
import { moneyText } from './moneyInput.ts';

/**
 * Marshalling in words (166, P-33): how an account, a step, a bank and the deadline are said. Pure, so it is tested
 * without a browser; the hooks are api/marshalling.ts.
 */

/** "...1234": the one way an account is shown (the agency's mask). */
export const lastFour = (last4: string) => `...${last4}`;

export const KIND_WORDS: Record<AccountKind, string> = {
  checking: 'Checking', savings: 'Savings', certificate_of_deposit: 'Certificate of deposit', money_market: 'Money market',
  brokerage: 'Brokerage', retirement: 'Retirement', cash_on_hand: 'Cash on hand', rep_payee: 'Representative payee',
  other: 'Other',
};

export const STEP_WORDS: Record<StepKind, string> = {
  appointment_set: 'Appointment set at the branch',
  documents_scanned: 'Documents brought and scanned',
  bank_review: 'The bank\'s review',
  return_visit: 'Return visit: each account\'s outcome',
  check_received: 'Check received',
  check_deposited: 'Check deposited',
  statements_received: 'Statements received',
  internal_move: 'Moved into a guardianship account at this bank',
  no_record: 'No record of the client at this bank',
  all_closed_before: 'All accounts closed before the guardianship',
};

/** The steps a bank's marshalling records, in the order they usually happen (shown, never enforced: Q2). */
export const STEP_ORDER: StepKind[] = ['appointment_set', 'documents_scanned', 'bank_review', 'return_visit', 'check_received',
  'check_deposited', 'statements_received', 'internal_move', 'no_record', 'all_closed_before'];

/** What a step form says about the step before it is recorded (Arthur's outcomes, in words). */
export const STEP_HINTS: Record<StepKind, string> = {
  appointment_set: 'The day of the appointment at the branch. It may be a day ahead.',
  documents_scanned: 'What was brought, and which order\'s certified copies, if you know.',
  bank_review: 'A refusal opens a task in Legal\'s queue; the bank\'s next answer closes it.',
  return_visit: 'Each account\'s outcome is a record only: the bank is marshalled by its check, its statements or a move.',
  check_received: 'Mark the bank\'s last check: the bank is marshalled the day its last check is in hand. A deposit is a confirmation and holds nothing up.',
  check_deposited: 'A confirmation of a check already in hand.',
  statements_received: 'The statements or the closing letter marshal the bank. Choose the document from the client\'s files once it has been scanned clean.',
  internal_move: 'The money moved into a new guardianship account at this bank: the bank is marshalled on that day, with no check.',
  // F-102 (175): the last bank to end completes the deadline by itself
  no_record: 'The bank holds no record of the client: it drops out and is not counted. If it is the last bank, "Marshal client accounts" completes.',
  // A-#569 (Q1 NO, Q1a a): "All accounts closed before" ends nothing; "Statements received", with its document, does
  all_closed_before: 'Every account was closed before the guardianship, or a zero-balance account was closed with no check: a Finance task asks for 5 years of statements, and the bank stays open until "Statements received" is recorded.',
};

/**
 * Where adding a bank sits (A-#576, Arthur, 31 October 2026): while "Marshal client accounts" is complete and a bank is
 * listed (not withdrawn), it moves to the bottom of the section and reads "New bank found", since a bank added then
 * reopens the task (F-102, 175). Otherwise, open or with no bank listed, it keeps its place in the bar and its label.
 */
export const ADD_BANK_LABEL = 'Add a bank or institution';
export const NEW_BANK_LABEL = 'New bank found';
export const NEW_BANK_HINT = 'Adding a bank opens "Marshal client accounts" again.';
/** F-110: how a deadline met by "No accounts found" ends its line (the words are one place, to change in one line). */
export const MET_BY_NO_ACCOUNTS = 'by "No accounts found"';
export function addBankPlace(d: MarshalDeadline | undefined,
  banks: readonly Pick<MarshalInstitution, 'withdrawn'>[]): 'bar' | 'bottom' {
  return d?.status === 'Complete' && banks.some((b) => !b.withdrawn) ? 'bottom' : 'bar';
}

/** The bank's state in words, from what the database derived. */
export function institutionState(i: MarshalInstitution, say: (d: string) => string): string {
  if (i.withdrawn) return 'Withdrawn: it counts for nothing.';
  if (i.dropped) return 'No record of the client: not counted.';
  if (i.marshalled_on && i.partial) return `Partly marshalled on ${say(i.marshalled_on)}, up to its order.`;
  if (i.marshalled_on) {
    const how = i.marshalled_how === 'last check' ? 'by its last check'
      : i.marshalled_how === 'statements' ? 'by its statements' : 'by an internal move';
    return `Marshalled on ${say(i.marshalled_on)} ${how}.`;
  }
  return 'In progress.';
}

/**
 * F-84 (A-#483, A-#488): "Certified copies brought" is a count, 0 to 99 (166's column); free text belongs in Other
 * documents. Number() of whatever was typed sent null for text, which the API refused as a step with no day.
 */
export const COPIES_WORDS = 'A whole number of copies, 0 to 99. Anything else goes in Other documents.';
export const copiesOk = (s: string) => s.trim() === '' || /^\d{1,2}$/.test(s.trim());

/** What the step form holds, as typed. */
export type StepDraft = {
  on_date: string; outcome: string; letter: boolean; copies: string; other: string; amount: string; last: boolean;
  partial: boolean; remaining: string; order: string; under: string; account: string; check: string; upload: string;
};

/**
 * The step's fields as the API takes them, or null while a box holds what the API would refuse (F-84). The one
 * builder: the form never sends Number() of what was typed. `refused`: the bank has a recorded refusal.
 */
export function stepFields(kind: StepKind, f: StepDraft, refused: boolean): Record<string, unknown> | null {
  const b: Record<string, unknown> = { on_date: f.on_date };
  if (kind === 'documents_scanned') {
    if (!copiesOk(f.copies)) return null;
    b.letter_brought = f.letter;
    if (f.copies.trim()) b.certified_copies = Number(f.copies.trim());
    if (f.other.trim()) b.other_text = f.other.trim();
    if (f.order) b.court_order_id = f.order;
  }
  if (kind === 'bank_review') { b.review_outcome = f.outcome; if (f.order && refused && f.outcome !== 'refused') b.court_order_id = f.order; }
  if (kind === 'check_received') {
    // F-100: typed with commas or "$" or not, an amount is sent as the plain number, through the one reader
    const amount = moneyText(f.amount);
    const remaining = moneyText(f.remaining);
    if (amount === null || (f.partial && f.remaining.trim() !== '' && remaining === null)) return null;
    b.amount = amount; b.is_last = f.partial ? true : f.last; b.is_partial = f.partial;
    if (f.partial) { b.court_order_id = f.order; if (remaining !== null) b.approx_remaining = remaining; }
  }
  if (kind === 'check_deposited') { b.check_action_id = f.check; b.account_id = f.account; }
  if (kind === 'internal_move') b.account_id = f.account;
  if (kind === 'statements_received') b.upload_id = f.upload;
  if (['check_received', 'statements_received', 'internal_move'].includes(kind) && f.under) b.under_order_id = f.under;
  return b;
}

/** What correcting a step changes, said before it is saved (A-#393 follow-up: each correction says so in words). */
export function stepCorrectionSays(kind: StepKind): string {
  switch (kind) {
    case 'check_received':
      return 'Correcting the check\'s date, or whether it is the last check from this bank, changes when the bank is marshalled, and "Marshal client accounts" follows.';
    case 'statements_received':
    case 'internal_move':
      return 'Correcting the date changes the day the bank is marshalled, and "Marshal client accounts" follows.';
    case 'no_record':
      return 'Correcting the date keeps the bank out of the count.';
    case 'bank_review':
      return 'Correcting the bank\'s answer can open or close Legal\'s task.';
    default:
      return 'The step is corrected as a new version, with the reason; the earlier version is kept.';
  }
}

/** What correcting an account changes (A-#393 follow-up). */
export const ACCOUNT_CORRECTION_SAYS = 'The account stays known by its last four only. Correcting the day it was found can '
  + 'open the bank again: an account found after the bank was marshalled has to be marshalled too.';

/**
 * A step's order in words, in the step's summary (F-85, A-#494 4): a bank's review ACTED ON an order; documents scanned
 * are the copies of one. A partial check says its order itself ("a partial, up to ..."); no other step carries one.
 */
export function stepOrderSaid(kind: StepKind, order: string): string | null {
  if (kind === 'bank_review') return `acted on ${order}`;
  if (kind === 'documents_scanned') return `the copies of ${order}`;
  return null;
}

/**
 * The deadline in words. Open, it says what completes it from the banks as they stand (F-91; P-33, Arthur's outcome
 * 2): with no bank listed it can complete only by "No accounts found", so it never says "every bank still counted".
 * With a bank listed it completes when every listed bank is marshalled or ended "No record" (F-102, 175).
 */
export function deadlineSaid(d: MarshalDeadline | undefined, banks: readonly Pick<MarshalInstitution, 'withdrawn' | 'dropped'>[],
  say: (d: string) => string): string {
  if (!d) return 'No "Marshal client accounts" deadline is on file for this client.';
  if (d.status === 'Complete') {
    // F-110: met by the records with no bank listed can only be "No accounts found" (175), so it says so
    if (d.met_by_marshalling && !banks.some((b) => !b.withdrawn)) {
      return `"Marshal client accounts" is complete: ${say(d.completed_on!)}, ${MET_BY_NO_ACCOUNTS}.`;
    }
    return d.met_by_marshalling ? `"Marshal client accounts" is complete: ${say(d.completed_on!)}, from the banks below.`
      : `"Marshal client accounts" was completed another way${d.completed_on ? ` on ${say(d.completed_on)}` : ''}.`;
  }
  const open = `"Marshal client accounts" is open, due ${say(d.due_date)}.`;
  const live = banks.filter((b) => !b.withdrawn);
  if (live.length === 0) return `${open} Add each bank that holds the client's money; if none does, record "No accounts found".`;
  // F-102 (175): with a bank listed, the last one ending "No record" completes it by itself, and "No accounts found"
  // is refused beside a listed bank, so there is one line
  return `${open} It completes itself when every bank still counted is marshalled or ends "No record of the client".`;
}
